<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import LoadingLogoSpinner from "@/components/Reusables/Loading/LoadingLogoSpinner.vue";

const PHRASES = ["Thinking", "Working on it", "Looking things up", "Pulling it together"];

// One 1 s tick drives the whole indicator: the phrase swap and the elapsed counter.
const PHRASE_SECONDS = 3;
// Late enough that a quick answer never flashes a counter, early enough to reassure on a slow one.
const ELAPSED_VISIBLE_AFTER_SECONDS = 3;
const SECONDS_PER_MINUTE = 60;

const elapsedSeconds = ref(0);
let interval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  interval = setInterval(() => {
    elapsedSeconds.value += 1;
  }, 1000);
});

onUnmounted(() => {
  if (interval !== null) {
    clearInterval(interval);
    interval = null;
  }
});

const phraseIndex = computed(() => Math.floor(elapsedSeconds.value / PHRASE_SECONDS) % PHRASES.length);

const showElapsed = computed(() => elapsedSeconds.value >= ELAPSED_VISIBLE_AFTER_SECONDS);

const elapsedLabel = computed(() => {
  const minutes = Math.floor(elapsedSeconds.value / SECONDS_PER_MINUTE);
  const seconds = elapsedSeconds.value % SECONDS_PER_MINUTE;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
});
</script>

<template>
  <div
    role="status"
    class="chat-thinking-block rounded-8 max-w-[85%] overflow-hidden border">
    <!-- aria-hidden: the counter changes every second and the phrase every three, which this live
         region would otherwise read out again and again. The sr-only line is the only thing spoken. -->
    <div
      aria-hidden="true"
      class="gap-px-8 px-px-12 py-px-8 flex items-center">
      <!-- The spinning gaiup mark is the loading signal: brand motion, and readable at a glance in a
           way the 8px pulse dot it replaces was not (RTLCA-5811). -->
      <LoadingLogoSpinner
        class="flex shrink-0 items-center"
        logo-class="icon-lg" />
      <Transition
        name="fade-in-out-quick"
        mode="out-in">
        <span
          :key="phraseIndex"
          class="chat-thinking-label t-caption-bold"
          >{{ PHRASES[phraseIndex] }}…</span
        >
      </Transition>
      <span
        v-if="showElapsed"
        class="chat-thinking-meta t-footnote ml-auto tabular-nums"
        >{{ elapsedLabel }}</span
      >
    </div>
    <span class="sr-only">AI Agent is thinking</span>
  </div>
</template>
